import { useQuery } from '@tanstack/react-query';
import { supabaseClient } from '../../utils/supabase';
import useAuth from '../../utils/useAuth';

/**
 * Hilton App Out (REG-918). The database decides everything: who may send
 * (public.appout_my_sender: the eleven Hilton reps, from their own
 * @hiltoncaps.com mailbox, no fallback), what the email says
 * (public.appout_preview renders it server-side) and the send itself
 * (public.appout_send: REG-857 recipient rules, sandbox, call log, Lead to
 * App Out). The frontend only shows the preview and asks for confirmation.
 */

export interface AppoutSender {
  brand: string;
  from_email: string;
  from_name: string;
}

export interface AppoutPreview {
  merchant: number;
  merchant_status: string | null;
  business_name: string | null;
  brand: string;
  from_email: string;
  from_name: string;
  to: string | null;
  to_options: string[];
  sandbox: boolean;
  delivered_to: string | null;
  blocked: 'no_address' | 'internal' | 'test_address' | 'dnd' | 'no_sandbox_recipient' | null;
  first_name: string;
  subject: string;
  html: string;
}

export interface AppoutSendResult {
  email_send_id: number;
  merchant: number;
  from: string;
  to: string;
  delivered_to: string;
  sandbox: boolean;
  subject: string;
  status_changed: boolean;
}

type RpcClient = {
  rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string; code?: string } | null }>;
};
const rpc = supabaseClient as unknown as RpcClient;

/** The caller's Hilton App Out mailbox, or null: no App Out button. */
export function useAppoutSender(): AppoutSender | null {
  const { appUser } = useAuth();
  const { data } = useQuery({
    queryKey: ['appout-sender', appUser?.id ?? null],
    enabled: Boolean(appUser?.id),
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data: sender, error } = await rpc.rpc('appout_my_sender');
      if (error) throw error;
      return (sender ?? null) as AppoutSender | null;
    },
  });
  return data ?? null;
}

export async function previewAppout(args: {
  merchantId: number;
  toEmail?: string | null;
  firstName?: string | null;
  subject?: string | null;
}): Promise<AppoutPreview> {
  const { data, error } = await rpc.rpc('appout_preview', {
    p_merchant: args.merchantId,
    p_to_email: args.toEmail || null,
    p_first_name: args.firstName ?? null,
    p_subject: args.subject || null,
  });
  if (error) throw new Error(error.message);
  return data as AppoutPreview;
}

export async function sendAppout(args: {
  merchantId: number;
  toEmail: string;
  firstName: string;
  subject: string;
}): Promise<AppoutSendResult> {
  const { data, error } = await rpc.rpc('appout_send', {
    p_merchant: args.merchantId,
    p_to_email: args.toEmail,
    p_first_name: args.firstName,
    p_subject: args.subject,
  });
  if (error) throw new Error(error.message);
  return data as AppoutSendResult;
}

export const BLOCKED_REASON: Record<NonNullable<AppoutPreview['blocked']>, string> = {
  no_address: 'This merchant has no email address. Add one to the merchant first.',
  internal: 'That is a company or rep address, not the merchant\'s. App Outs only go to merchants.',
  test_address: 'That is a test address. App Outs are not sent to it.',
  dnd: 'This merchant is Do Not Disturb. App Outs are not sent to DND merchants.',
  no_sandbox_recipient: 'The sandbox recipient is not configured, so nothing can be sent.',
};
